import { useCallback, useState, useRef, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { BoxList } from './BoxList'
import { AddBoxButton } from './AddBoxButton'
import { BoxConfirmDialog } from './BoxConfirmDialog'
import { MAX_BOXES } from './constants'
import type { BoxesTabProps, StoryBox } from './types'
import { EmptyState } from '../../shared/EmptyState'

/**
 * BoxesTab - Story boxes settings tab
 */
export function BoxesTab({
  boxes,
  onChange
}: BoxesTabProps) {
  const [confirmDialog, setConfirmDialog] = useState<{ boxId: string; boxNumber: number } | null>(null)

  // CRITICAL: Use ref to always access latest boxes in callbacks
  // This prevents stale closure bugs where debounced saves from one box
  // overwrite content from another box that hasn't saved yet
  const boxesRef = useRef(boxes)
  useEffect(() => {
    boxesRef.current = boxes
  }, [boxes])

  // Add new box
  const handleAddBox = useCallback(() => {
    const currentBoxes = boxesRef.current
    if (currentBoxes.length >= MAX_BOXES) return

    const newBox: StoryBox = {
      id: uuidv4(),
      order: currentBoxes.length + 1,
      content: ''
    }

    onChange([...currentBoxes, newBox])
  }, [onChange])

  // Update box content - uses ref to avoid stale closure
  const handleContentChange = useCallback(
    (id: string, content: string) => {
      const currentBoxes = boxesRef.current
      const updatedBoxes = currentBoxes.map((box) => (box.id === id ? { ...box, content } : box))
      onChange(updatedBoxes)
    },
    [onChange]
  )

  // Actually remove the box and re-number remaining boxes
  const removeBox = useCallback(
    (id: string) => {
      const currentBoxes = boxesRef.current
      const filteredBoxes = currentBoxes.filter((b) => b.id !== id)
      // Re-number boxes to maintain sequential order
      const renumberedBoxes = filteredBoxes.map((box, index) => ({
        ...box,
        order: index + 1
      }))
      onChange(renumberedBoxes)
      setConfirmDialog(null)
    },
    [onChange]
  )

  // Remove box (with confirmation if it has content)
  const handleRemove = useCallback(
    (id: string) => {
      const currentBoxes = boxesRef.current
      const boxToRemove = currentBoxes.find((b) => b.id === id)
      if (!boxToRemove) return

      // Show confirmation dialog if box has content
      if (boxToRemove.content.trim()) {
        setConfirmDialog({ boxId: id, boxNumber: boxToRemove.order })
      } else {
        // Remove immediately if empty
        removeBox(id)
      }
    },
    [removeBox]
  )

  // Confirm removal from dialog
  const handleConfirmRemove = useCallback(() => {
    if (confirmDialog) {
      removeBox(confirmDialog.boxId)
    }
  }, [confirmDialog, removeBox])

  // Close dialog
  const handleCloseDialog = useCallback(() => {
    setConfirmDialog(null)
  }, [])

  // Detect if boxes are truly empty (no boxes at all)
  const isEmpty = boxes.length === 0

  return (
    <div data-testid="boxes-tab" className="space-y-6">
      {isEmpty ? (
        <EmptyState
          icon="📦"
          title="No Story Boxes"
          description="Story boxes help you map out your song's narrative progression. Add boxes to outline key moments in your story."
          action={{
            label: 'Add First Box',
            onClick: handleAddBox
          }}
        />
      ) : (
        <>
          {/* Tab Header with Description */}
          <div className="text-sm text-gray-400">
            <p>
              Map out the narrative progression of your song using visual boxes. Each box represents a key moment or
              development in the story (typically 1-5 boxes).
            </p>
          </div>

          {/* Box List */}
          <BoxList boxes={boxes} onContentChange={handleContentChange} onRemove={handleRemove} />

          {/* Add Box Button */}
          <AddBoxButton onAdd={handleAddBox} disabled={boxes.length >= MAX_BOXES} />
        </>
      )}

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <BoxConfirmDialog
          isOpen={true}
          onClose={handleCloseDialog}
          onConfirm={handleConfirmRemove}
          boxNumber={confirmDialog.boxNumber}
        />
      )}
    </div>
  )
}
